// TabComponents/AgentInfoTab.jsx
import React from 'react';
import { useDomainGroups } from '../../../../Context/DomainGroupsContext';
import { agentGroupNames } from '../../../../Utils';
import {
    Card, Row, Col, Typography, Tag, Descriptions,
    Avatar, Badge, Button, List, Alert, Space, Divider,
    Timeline, Table, Tooltip
} from 'antd';
import {
    UserOutlined, HistoryOutlined, SwapOutlined,
    IdcardOutlined, CheckCircleOutlined, CloseCircleOutlined,
    CrownOutlined, MailOutlined, PhoneOutlined,
    TeamOutlined, PhoneFilled, MailFilled, UserSwitchOutlined,
    RightOutlined, CalendarOutlined, ClockCircleOutlined,
    FieldTimeOutlined, ArrowDownOutlined, ArrowUpOutlined,
    LockOutlined,
    TagOutlined,
    TrophyOutlined,
    StarOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import { PACIFIC_TZ, getAssignmentTimes, wasQueuedOnArrival } from '../assignmentTimes';

const { Title, Text } = Typography;

const AgentInfoTab = ({
    lead,
    agentMap,
    userData,
    isAgentAssigned,
    onTransferAgent,
    agentDetails = {}
}) => {
    // The groups an agent's memberships resolve to, for the labels below.
    const { groups } = useDomainGroups();
        const isSalesMade = lead?.leadStage === "Sales Made";
const currentUserRole = userData?.role; // or wherever you get user role from
const isAdmin = currentUserRole === 'ADMIN' ;
    const resolveAgentId = (agentRef) => {
        if (!agentRef) {
            return null;
        }

        if (typeof agentRef === 'string') {
            return agentRef;
        }

        return agentRef._id || agentRef.id || null;
    };

    const getAgentName = (agentRef) => {
        if (!agentRef || typeof agentRef !== 'object') {
            return null;
        }

        const fullName = [agentRef.firstName, agentRef.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();

        return fullName || agentRef.userName || null;
    };

    // Helper function to get agent details
    const getAgentDetails = (agentRef) => {
        const agentId = resolveAgentId(agentRef);

        if (agentId && agentDetails && agentDetails[agentId]) {
            const agent = agentDetails[agentId];
            return {
                name: `${agent.firstName} ${agent.lastName}` || "No Agent",
    email: agent.email || `${agentId}@company.com`,
    phone: agent.phone || 'Not available',
    role: agent.role || 'Queue',
    avatarInitial: (agent.firstName?.charAt(0) || agent.userName?.charAt(0) || 'C').toUpperCase(),
    status: agent.status || 'ACTIVE',
    extension: agent.extension || 'N/A',
    userName: agent.userName || 'N/A',
    directNumber: agent.directNumber || 'N/A',
    subRole: agent.subRole || 'N/A',
    isPerformance: agent.isPerformance || false,
    domainGroups: agent.domainGroups || [],
    subset1: agent.subset1 || false,
    subset2: agent.subset2 || false,
    isDefaultTierEligible: agent.isDefaultTierEligible !== false,
    premium: agent.premium || false
            };
        }

        if (agentRef && typeof agentRef === 'object') {
            const name = getAgentName(agentRef) || (agentId ? agentMap[agentId] : null) || 'Unknown Agent';

            return {
                name,
                email: agentRef.email || (agentId ? `${agentId}@company.com` : 'Not available'),
                phone: agentRef.directNumber || agentRef.phone || 'Not available',
                role: agentRef.role || 'SALES-AGENT',
                avatarInitial: (
                    agentRef.firstName?.charAt(0) ||
                    agentRef.userName?.charAt(0) ||
                    name.charAt(0) ||
                    'A'
                ).toUpperCase(),
                status: agentRef.status || (agentRef.isActive === false ? 'INACTIVE' : 'ACTIVE'),
                subRole: agentRef.subRole || 'N/A',
                domainGroups: agentRef.domainGroups || []
            };
        }

     const fallbackName = (agentId && agentMap[agentId]) || 'Unknown Agent';
return {
    name: fallbackName,
    email: agentId ? `${agentId}@company.com` : 'Not available',
    phone: 'Not available',
    role: 'SALES-AGENT',
    avatarInitial: fallbackName.charAt(0).toUpperCase(),
    status: 'ACTIVE',
    extension: 'N/A',
    userName: fallbackName,
    directNumber: 'Not available',
    subRole: 'N/A',
    isPerformance: false,
    domainGroups: [],
    subset1: false,
    subset2: false,
    isDefaultTierEligible: false,
    premium: false
};
    };

    // Format date (US Pacific, whatever timezone the PC is set to)
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: PACIFIC_TZ
        });
    };

    // Format time ago - more detailed version
    const getTimeAgo = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;

        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffYears > 0) {
            return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
        } else if (diffMonths > 0) {
            return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
        } else if (diffWeeks > 0) {
            return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
        } else if (diffDays > 0) {
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        } else if (diffHours > 0) {
            return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        } else if (diffMinutes > 0) {
            return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    };

    // Sort transfer history in descending order (newest first)
// Safer way to handle transfer history
const getTransferHistory = () => {
    // Check if transferHistory exists and is an array
    if (!lead?.transferHistory) return [];
    
    // If it's already an array, use it
    if (Array.isArray(lead.transferHistory)) {
        return lead.transferHistory;
    }
    
    // If it's a single object, wrap it in an array
    if (typeof lead.transferHistory === 'object' && lead.transferHistory !== null) {
        return [lead.transferHistory];
    }
    
    // Default fallback
    return [];
};

const transferHistoryArray = getTransferHistory();

const sortedTransferHistory = [...transferHistoryArray].sort((a, b) => {
    return new Date(b.transferredAt) - new Date(a.transferredAt);
});

// Row 0: who the lead was first assigned to. Only when the oldest transfer names a fromAgent
// and the lead was not queued on arrival (a transfer out of the queue can name the customer's
// previous agent); otherwise the first transfer already is the first assignment.
const oldestTransfer = sortedTransferHistory[sortedTransferHistory.length - 1];
const firstAssignmentRow = oldestTransfer?.fromAgent && !wasQueuedOnArrival(lead) ? {
    key: 'first-assignment',
    isFirstAssignment: true,
    toAgent: oldestTransfer.fromAgent,
    transferredAt: getAssignmentTimes(lead).firstAssignedAt
} : null;
    // Transfer History Columns for Table
    const transferColumns = [
        {
            title: 'No.',
            key: 'index',
            width: 60,
            render: (_, record, index) => record.isFirstAssignment ? (
                <Badge count={0} showZero style={{ backgroundColor: '#bfbfbf', fontSize: '10px' }} />
            ) : (
                <Badge
                    count={sortedTransferHistory.length - index}
                    style={{
                        backgroundColor: index === 0 ? '#52c41a' : index === 1 ? '#1890ff' : '#722ed1',
                        fontSize: '10px'
                    }}
                />
            )
        },
        {
            title: 'From Agent',
            dataIndex: 'fromAgent',
            key: 'fromAgent',
            render: (agentId, record) => {
                if (record.isFirstAssignment) {
                    return (
                        <Text type="secondary" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                            Lead created
                        </Text>
                    );
                }
                const details = getAgentDetails(agentId);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar
                            size="small"
                            style={{
                                backgroundColor: '#f56a00',
                                fontSize: '10px',
                                fontWeight: 'bold'
                            }}
                        >
                            {details.avatarInitial}
                        </Avatar>
                        <div>
                            <Text strong style={{ fontSize: '13px' }}>
                                {details.name}
                            </Text>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                                {details.role}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            title: '',
            key: 'arrow',
            width: 40,
            align: 'center',
            render: () => (
                <RightOutlined style={{
                    color: '#1890ff',
                    fontSize: '12px'
                }} />
            )
        },
        {
            title: 'To Agent',
            dataIndex: 'toAgent',
            key: 'toAgent',
            render: (agentId) => {
                const details = getAgentDetails(agentId);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar
                            size="small"
                            style={{
                                backgroundColor: '#52c41a',
                                fontSize: '10px',
                                fontWeight: 'bold'
                            }}
                        >
                            {details.avatarInitial}
                        </Avatar>
                        <div>
                            <Text strong style={{ fontSize: '13px' }}>
                                {details.name}
                            </Text>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                                {details.role}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Date',
            dataIndex: 'transferredAt',
            key: 'transferredAt',
            width: 180,
            render: (date) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CalendarOutlined style={{ color: '#1890ff', fontSize: '10px' }} />
                        <Text style={{ fontSize: '11px', fontWeight: 500 }}>
                            {formatDate(date)}
                        </Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <ClockCircleOutlined style={{ color: '#666', fontSize: '10px' }} />
                        <Text type="secondary" style={{ fontSize: '10px' }}>
                            {new Date(date).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: PACIFIC_TZ
                            })}
                        </Text>
                    </div>
                </div>
            )
        },
        {
            title: 'Show Now',
            dataIndex: 'transferredAt',
            key: 'timeAgo',
            width: 120,
            render: (date) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    backgroundColor: date === 0 ? '#e6f7ff' : '#fafafa',
                    borderRadius: '4px',
                    border: '1px solid #f0f0f0'
                }}>
                    <FieldTimeOutlined style={{
                        color: date === 0 ? '#1890ff' : '#666',
                        fontSize: '12px'
                    }} />
                    <Text style={{
                        fontSize: '12px',
                        fontWeight: date === 0 ? 600 : 500,
                        color: date === 0 ? '#1890ff' : '#666'
                    }}>
                        {getTimeAgo(date)}
                    </Text>
                </div>
            )
        },
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_, record, index) => record.isFirstAssignment ? (
                <Tag style={{ fontSize: '10px', borderRadius: '12px', padding: '2px 8px', color: '#8c8c8c' }}>
                    Start
                </Tag>
            ) : (
                <Tag
                    color={index === 0 ? 'success' : 'default'}
                    style={{
                        fontSize: '10px',
                        borderRadius: '12px',
                        padding: '2px 8px'
                    }}
                >
                    {index === 0 ? 'Latest' : 'Previous'}
                </Tag>
            )
        }
    ];

    return (
        <Card
            style={{
                marginBottom: '24px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)'
            }}
        >

            {/* Assigned Agent Section */}
            {isAgentAssigned && (
                <Card
                    style={{
                        borderRadius: '10px',
                        borderLeft: '4px solid #1890ff',
                        background: 'white',
                        marginBottom: '20px'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <UserOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                                Current Agent
                            </Title>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Tag color="blue" icon={<CheckCircleOutlined />}>
                                {getAgentDetails(lead.handledBy).status}
                            </Tag>
                            {!isSalesMade ? (
                    <Button
                        type="primary"
                        icon={<UserSwitchOutlined />}
                        onClick={() => onTransferAgent && onTransferAgent(lead)}
                        style={{
                            background: 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: 500
                        }}
                    >
                        Transfer Agent
                    </Button>
                ) : (
                    <Tooltip title="Cannot transfer agent after Sales Made stage">
                        <Button
                            type="primary"
                            icon={<LockOutlined/>}

                            disabled
                            style={{
                                background: '#d9d9d9',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: 500
                            }}
                        >
                            Transfer Agent
                        </Button>
                    </Tooltip>
                )}
                                       </div>
                    </div>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px',
                                padding: '16px',
                                backgroundColor: '#e6f7ff',
                                borderRadius: '8px'
                            }}>
                                <Avatar
                                    size={72}
                                    style={{
                                        background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
                                        fontSize: '28px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 12px rgba(24,144,255,0.3)'
                                    }}
                                >
                                    {getAgentDetails(lead.handledBy).avatarInitial}
                                </Avatar>

                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                                            {getAgentDetails(lead.handledBy).name}
                                        </Title>
                                        <Tag
                                            color="blue"
                                            icon={<TeamOutlined />}
                                            style={{ fontSize: '12px', marginTop: '4px' }}
                                        >
                                            {getAgentDetails(lead.handledBy).role}
                                        </Tag>
                                    </div>

                                    <Divider style={{ margin: '12px 0' }} />

                                 <Space size="large" wrap direction="vertical" style={{ width: '100%' }}>
    {/* Basic Info Row */}
    <Space size="large" wrap>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MailFilled style={{ color: '#1890ff' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                {getAgentDetails(lead.handledBy).email}
            </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PhoneFilled style={{ color: '#1890ff' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                {getAgentDetails(lead.handledBy).phone}
            </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserOutlined style={{ color: '#1890ff' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                {getAgentDetails(lead.handledBy).userName}
            </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PhoneOutlined style={{ color: '#1890ff' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                Ext: {getAgentDetails(lead.handledBy).extension}
            </Text>
        </div>
    </Space>

    {/* Admin Only Fields */}
    {isAdmin && (
        <>
            <Divider style={{ margin: '8px 0' }} />
            <Space size="large" wrap>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TagOutlined style={{ color: '#722ed1' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Sub Role: {getAgentDetails(lead.handledBy).subRole}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TrophyOutlined style={{ color: '#722ed1' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Performance: {getAgentDetails(lead.handledBy).isPerformance ? 'Yes' : 'No'}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <StarOutlined style={{ color: '#722ed1' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Premium: {getAgentDetails(lead.handledBy).premium ? 'Yes' : 'No'}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <AppstoreOutlined style={{ color: '#722ed1' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Domain Groups: {agentGroupNames(getAgentDetails(lead.handledBy), groups).join(', ') || 'None'}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircleOutlined style={{ color: '#722ed1' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Tier Eligible: {getAgentDetails(lead.handledBy).isDefaultTierEligible ? 'Yes' : 'No'}
                    </Text>
                </div>
            </Space>
        </>
    )}
</Space>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}
 {lead.serviceHandledBy && (
        <Descriptions.Item label="Service Handler">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TeamOutlined />
                {(() => {
                    const serviceHandlerId = lead.serviceHandledBy?._id || lead.serviceHandledBy;
                    const serviceHandlerName = agentMap[serviceHandlerId] || 
                        (lead.serviceHandledBy?.firstName ? 
                            `${lead.serviceHandledBy.firstName} ${lead.serviceHandledBy.lastName}` : 
                            'Unknown');
                    return serviceHandlerName;
                })()}
                {lead.handledBy !== lead.serviceHandledBy && (
                    <Tag color="orange" style={{ fontSize: '11px' }}>
                        Different from Agent
                    </Tag>
                )}
            </div>
        </Descriptions.Item>
    )}
            {/* Not Assigned Alert */}
            {!isAgentAssigned && (
                <Alert
                    message="No Agent Assigned"
                    description="This lead is currently not assigned to any sales agent. Please assign an agent to proceed with follow-up."
                    type="warning"
                    showIcon
                    style={{ marginBottom: '20px' }}
                    action={
                        <Button
                            type="primary"
                            icon={<UserSwitchOutlined />}
                            onClick={() => onTransferAgent && onTransferAgent(lead)}
                            style={{
                                background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                                border: 'none',
                                fontWeight: 500
                            }}
                        >
                            Assign Agent
                        </Button>
                    }
                />
            )}

            {/* Transfer History Section */}
            {sortedTransferHistory.length > 0 && (
                <Card
                    style={{
                        borderRadius: '10px',
                        borderLeft: '4px solid #722ed1',
                        background: 'white',
                        marginBottom: '20px'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <HistoryOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                            <Title level={5} style={{ margin: 0, color: '#722ed1' }}>
                                Transfer History
                            </Title>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <ArrowDownOutlined style={{ color: '#722ed1', fontSize: '14px' }} />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Newest First
                                </Text>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Tag color="purple" icon={<SwapOutlined />}>
                                {sortedTransferHistory.length} Transfer{sortedTransferHistory.length !== 1 ? 's' : ''}
                            </Tag>
                            <Tag color="green" icon={<FieldTimeOutlined />}>
                                Latest: {getTimeAgo(sortedTransferHistory[0]?.transferredAt)}
                            </Tag>
                        </div>
                    </div>


                    {/* Table View */}
                    <Table
                        columns={transferColumns}
                        dataSource={sortedTransferHistory.map((transfer, index) => ({
                            key: index,
                            fromAgent: transfer.fromAgent,
                            toAgent: transfer.toAgent,
                            transferredAt: transfer.transferredAt,
                            _id: transfer._id
                        })).concat(firstAssignmentRow ? [firstAssignmentRow] : [])}
                        pagination={sortedTransferHistory.length > 10 ? {
                            pageSize: 10,
                            showSizeChanger: false,
                            showQuickJumper: false,
                            simple: true
                        } : false}
                        size="small"
                        style={{
                            marginBottom: '16px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                        rowClassName={(record, index) =>
                            index === 0 ? 'latest-transfer-row' : ''
                        }
                        onRow={(record) => (record.isFirstAssignment ? { style: { opacity: 0.7, background: '#fafafa' } } : {})}
                    />

                    {/* Quick Stats */}
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: '#fafafa',
                        borderRadius: '6px',
                        marginTop: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                <SwapOutlined style={{ marginRight: '4px' }} />
                                Transfer frequency: Every {sortedTransferHistory.length > 1 ?
                                    Math.floor(
                                        (new Date(sortedTransferHistory[0].transferredAt) - new Date(sortedTransferHistory[sortedTransferHistory.length - 1].transferredAt)) /
                                        (1000 * 60 * 60 * 24 * sortedTransferHistory.length)
                                    ) + ' days' : 'N/A'
                                }
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Last updated: {getTimeAgo(lead?.updatedAt)}
                            </Text>
                        </div>
                    </div>
                </Card>
            )}

            {/* Previous Agent Section */}
            {lead?.previousAgent && (
                <Card
                    style={{
                        marginBottom: '20px',
                        borderRadius: '10px',
                        borderLeft: '4px solid #fa8c16',
                        background: 'white'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <HistoryOutlined style={{ color: '#fa8c16', fontSize: '20px' }} />
                            <Title level={5} style={{ margin: 0, color: '#fa8c16' }}>
                                Previous Agent
                            </Title>
                        </div>
                        <Tag color="orange" icon={<HistoryOutlined />}>
                            Previously Assigned
                        </Tag>
                    </div>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px',
                                padding: '16px',
                                backgroundColor: '#fff7e6',
                                borderRadius: '8px'
                            }}>
                                <Avatar
                                    size={72}
                                    style={{
                                        background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                                        fontSize: '28px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 12px rgba(250,140,22,0.3)'
                                    }}
                                >
                                    {getAgentDetails(lead.previousAgent).avatarInitial}
                                </Avatar>

                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Title level={4} style={{ margin: 0, color: '#fa8c16' }}>
                                            {getAgentDetails(lead.previousAgent).name}
                                        </Title>
                                        <Tag
                                            color="orange"
                                            icon={<TeamOutlined />}
                                            style={{ fontSize: '12px', marginTop: '4px' }}
                                        >
                                            {getAgentDetails(lead.previousAgent).role}
                                        </Tag>
                                    </div>

                                    <Divider style={{ margin: '12px 0' }} />
<Space size="large" wrap direction="vertical" style={{ width: '100%' }}>
    {/* Basic Info Row */}
    <Space size="large" wrap>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MailFilled style={{ color: '#fa8c16' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                {getAgentDetails(lead.previousAgent).email}
            </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PhoneFilled style={{ color: '#fa8c16' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                {getAgentDetails(lead.previousAgent).phone}
            </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserOutlined style={{ color: '#fa8c16' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                {getAgentDetails(lead.previousAgent).userName}
            </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PhoneOutlined style={{ color: '#fa8c16' }} />
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                Ext: {getAgentDetails(lead.previousAgent).extension}
            </Text>
        </div>
    </Space>

    {/* Admin Only Fields */}
    {isAdmin && (
        <>
            <Divider style={{ margin: '8px 0' }} />
            <Space size="large" wrap>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TagOutlined style={{ color: '#d46b00' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Sub Role: {getAgentDetails(lead.previousAgent).subRole}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TrophyOutlined style={{ color: '#d46b00' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Performance: {getAgentDetails(lead.previousAgent).isPerformance ? 'Yes' : 'No'}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <StarOutlined style={{ color: '#d46b00' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Premium: {getAgentDetails(lead.previousAgent).premium ? 'Yes' : 'No'}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <AppstoreOutlined style={{ color: '#d46b00' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Domain Groups: {agentGroupNames(getAgentDetails(lead.previousAgent), groups).join(', ') || 'None'}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircleOutlined style={{ color: '#d46b00' }} />
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Tier Eligible: {getAgentDetails(lead.previousAgent).isDefaultTierEligible ? 'Yes' : 'No'}
                    </Text>
                </div>
            </Space>
        </>
    )}
</Space>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* No Transfer History Message */}

        </Card>
    );
};

export default AgentInfoTab;
